import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	Heading,
	HStack,
	SimpleGrid,
	Stack,
	Text,
} from '@chakra-ui/react';
import BasicListingCard from 'components/Listing/BasicListingCard/BasicListingCard';
import { DedupListingModel } from 'models/Dedup/DedupListing';

interface DedupListingCardProps {
	dedupListing: DedupListingModel;
	onApprove: (id: string) => void;
	onReject: (id: string) => void;
	isApproving?: boolean;
	isRejecting?: boolean;
}

function getStatusColor(status: string): string {
	switch (status) {
		case 'Approved':
			return 'green';
		case 'Rejected':
			return 'red';
		default:
			return 'orange';
	}
}

function formatScore(value?: number): string {
	if (value === undefined || value === null) {
		return 'N/A';
	}
	return `${Math.round(value * 10000) / 10000}`;
}

export default function DedupListingCard({ dedupListing, onApprove, onReject, isApproving = false, isRejecting = false }: DedupListingCardProps) {
	return (
		<Card variant="outline" borderRadius="lg">
			<CardHeader borderBottomWidth="1px">
				<Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4}>
					<Stack spacing={1} alignItems="flex-start">
						<Heading fontSize="md">Potential Duplicate Listings</Heading>
						<HStack spacing={3}>
							<Badge colorScheme={getStatusColor(dedupListing.status)}>{dedupListing.status}</Badge>
							<Text fontSize="sm" color="gray.600">Name Score: {formatScore(dedupListing.similarityScore?.name)}</Text>
							<Text fontSize="sm" color="gray.600">Image Score: {formatScore(dedupListing.similarityScore?.image)}</Text>
						</HStack>
					</Stack>
					{dedupListing.isPending() && (
						<HStack spacing={2}>
							<Button colorScheme="green" size="sm" isLoading={isApproving} onClick={() => onApprove(dedupListing.id)}>Approve</Button>
							<Button colorScheme="red" size="sm" isLoading={isRejecting} onClick={() => onReject(dedupListing.id)}>Reject</Button>
						</HStack>
					)}
				</Flex>
			</CardHeader>
			<CardBody>
				<Stack spacing={6}>
					<Box>
						<Heading fontSize="sm" mb={2}>Primary Listing</Heading>
						{dedupListing.listing ? (
							<BasicListingCard listing={dedupListing.listing} showLabel={true} />
						) : (
							<Text fontSize="sm" color="gray.500">No primary listing data available.</Text>
						)}
					</Box>
					<Box>
						<Heading fontSize="sm" mb={2}>Potential Matches</Heading>
						{dedupListing.dedupListings.length ? (
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								{dedupListing.dedupListings.map((listing) => (
									<Card key={listing.id} variant="outline">
										<CardBody>
											<BasicListingCard listing={listing} showLabel={true} />
										</CardBody>
									</Card>
								))}
							</SimpleGrid>
						) : (
							<Text fontSize="sm" color="gray.500">No duplicate listings found.</Text>
						)}
					</Box>
				</Stack>
			</CardBody>
		</Card>
	);
}
